import ComposerAgentControls from './agents/ComposerAgentControls';
import React, { useState, useRef, useEffect } from 'react';
import LaneMenu, { type LaneVisibility } from './LaneMenu';

// ─────────────────────────────────────────────────────────────────────────────
// InputBox — CI-6.2 — iOS chat bar shape.
//
// Function: untouched, full stop. Every existing handler verbatim:
//   handleSend, handlePaperclipClick, handleFileSelect, handleRemoveStagedFile,
//   handlePaste, handleDriveUploadClick, handleDriveFileSelect, handleKeyDown.
//   Existing local state preserved too (message, isUploading, stagedFiles,
//   isDriveUploading, driveUploadStatus). Refs preserved.
//
// EXCEPT the hold-to-talk dictation button, RETIRED 2026-08-14 — the handlers,
// its `isListening` state and its `recognitionRef` went with it. Its place on an
// agent surface belongs to a call button. Full reasoning at the removal site.
//
// UI restructure: the heavy "row of square buttons" bar → an iOS InputBar with
//   • an opaque card container (matches iOS REXY_TOKENS.card) with a thin
//     domain-accent top hairline (the only place the accent comes in on this bar)
//   • a rounded-pill textarea up top, accent-tinted border (subtle off, brighter
//     on focus)
//   • a single toolbar row below: + attach · drive (Spencer only) ·
//     model-pill · spacer · ⋯ overflow · send (orange #f97316 circle, ↑)
//     (the mic sat between drive and the model pill until 2026-08-14)
//   • model dropdown anchored above the model pill (existing showModelMenu state)
//   • API override dropdown anchored above the overflow (existing showOverrideMenu)
//   • ⋯ overflow menu houses: Memory Override toggle, API Override (opens the
//     dropdown above), Frontier indicator (info-only when status.frontier_api),
//     Memory Manager (opens overlay), Security Dashboard (opens overlay), Quit
//
// All values for the relocated controls come in as props from App.tsx (same
// state that drove the status bar previously). InputBox holds ONE new piece of
// local UI state — `showOverflowMenu` — purely the overflow open/close. App.tsx
// state, handlers, and effects are not moved. (Flag 1 invariant is about
// ChatSurface, not InputBox; InputBox is allowed its own local UI state for
// truly-local concerns like its text-input value.)
// ─────────────────────────────────────────────────────────────────────────────

interface InputBoxProps {
  // ── existing (preserved verbatim) ──
  onSendMessage: (message: string) => void;
  // Widened signature to match App.tsx handleOverrideToggle (was (boolean) on
  // the old InputBox prop; the new toolbar actually uses the api arg).
  onOverrideToggle: (active: boolean, api?: string) => void;
  overrideMode: boolean;
  onFileUpload?: (file: File) => void;
  onSendWithFiles?: (message: string, files: File[]) => void;
  onSendWithFile?: (message: string, file: File) => void;
  activeAgent?: string | null;
  // Frontier lanes (Phase 4): the chat's own domain, and its message count (a
  // reply landed) — the lane dropdown's identity and re-read signal.
  activeDomain?: string | null;
  turnCount?: number;
  authToken?: string | null;
  // 6.8 — when true, the file-attach affordance is hidden entirely (no paperclip,
  // no staged-files row). The homepage Rexy chat sets this: backend walls HOMEPAGE
  // uploads, so surfacing an attach button there would be a confusing pseudo-path.
  // Optional, defaults off — every existing call site is unaffected.
  uploadsDisabled?: boolean;

  // (No accent props on InputBox — iOS InputBar declares them as vestigial but
  // never uses them visually; the accent in iOS chat lives in the header chip +
  // bubbles. On desktop the AgentTabBar above the chat + the bubbles carry it.
  // Mirroring iOS faithfully: input bar is neutral.)

  // ── CI-6: model selector (lifted from status bar) ──
  activeModel: string;
  setActiveModel: (m: string) => void;
  showModelMenu: boolean;
  setShowModelMenu: React.Dispatch<React.SetStateAction<boolean>>;
  ALL_MODELS: string[];

  // ── CI-6: API override (lifted from status bar). NOTE: F1 Build B retired the
  //     UI routing role of these — routing now rides the three-state switch below.
  //     Props kept on the interface for wiring compatibility; the override
  //     dropdown UI is removed. ──
  overrideApi: string | null;
  showOverrideMenu: boolean;
  setShowOverrideMenu: React.Dispatch<React.SetStateAction<boolean>>;

  // ── The OS-wide escalation toggle → `routing_mode` on /api/query ──
  //   OFF (default) ⇒ local_only     — nothing reaches the API
  //   ON            ⇒ local_frontier — local first, frontier when needed
  // The model pill (left) is the separate WHICH-model parameter, applied only when
  // the API is actually called. routingMode is DERIVED — the UI no longer SETS it.
  routingMode: 'local_only' | 'local_frontier';
  // THE OS-WIDE ESCALATION TOGGLE. Two states, default OFF, server-held.
  escalation: boolean;
  escalationLoaded: boolean;   // false until the server has answered — never guess
  localLaneUp: boolean;        // REPORTED, not offered: offline is a state, not a choice
  toggleEscalation: (next: boolean) => void | Promise<void>;

  // ── D4: local-down fallback dropdown → `fallback_target` on /api/query ──
  //   claude — fall back to Claude if local is down (DEFAULT — keeps the OS alive)
  //   gpt    — fall back to GPT if local is down
  //   none   — no fallback (air-gap / true local-only): local down → clean dead-end
  // Independent of the three-state routing switch: routing decides IF/HOW the
  // frontier is used in normal flow; this decides WHERE we land when local is down.
  fallbackTarget: 'claude' | 'gpt' | 'none';
  setFallbackTarget: React.Dispatch<
    React.SetStateAction<'claude' | 'gpt' | 'none'>
  >;

  // ── CI-6: memory override (lifted from status bar) ──
  memoryOverride: boolean;
  onMemoryOverrideToggle: () => void;

  // ── CI-6: frontier indicator (lifted from status bar) ──
  status?: { frontier_api?: string; brain_mode?: string } | null;

  // ── CI-6: memory manager + security + quit (lifted from status bar) ──
  setShowMemoryManager: (v: boolean) => void;
  onFetchMemory: (domain?: string) => void;
  memoryDomain: string;
  onOpenSecurity: () => void;
  onQuit: () => void;
}

// Model pill labels — mirrors the old status-bar mapping.
function modelPill(activeModel: string): string {
  return activeModel === 'nvidia/Qwen3.6-35B-A3B-NVFP4' ? 'LOCAL' :
    activeModel === 'claude-opus-4-7' ? 'OPUS 4.7' :
    activeModel === 'claude-sonnet-4-6' ? 'SONNET 4.6' :
    activeModel === 'claude-haiku-4-5' ? 'HAIKU 4.5' :
    activeModel === 'gpt-5.5' ? 'GPT-5.5' :
    activeModel === 'gpt-5.4' ? 'GPT-5.4' :
    activeModel === 'gpt-5.4-mini' ? 'GPT-5.4 MINI' :
    activeModel === 'gpt-5.1-codex' ? 'CODEX 5.1' :
    activeModel === 'gpt-5.1-codex-mini' ? 'CODEX MINI' :
    activeModel.toUpperCase();
}

function modelDotColor(model: string): string {
  return model === 'nvidia/Qwen3.6-35B-A3B-NVFP4' ? 'bg-green-400/70' :
    model.includes('opus') ? 'bg-purple-400/70' :
    model.includes('sonnet') ? 'bg-blue-400/70' :
    model.includes('haiku') ? 'bg-blue-400/70' :
    model.includes('gpt') ? 'bg-emerald-400/70' : 'bg-rexy-muted/40';
}

function modelFullLabel(model: string): string {
  return model === 'nvidia/Qwen3.6-35B-A3B-NVFP4' ? 'Local — Qwen (free)' :
    model === 'claude-haiku-4-5' ? 'Claude Haiku 4.5 (fast)' :
    model === 'claude-sonnet-4-6' ? 'Claude Sonnet 4.6' :
    model === 'claude-opus-4-7' ? 'Claude Opus 4.7 (best)' :
    model === 'gpt-5.4' ? 'GPT-5.4' :
    model === 'gpt-5.4-mini' ? 'GPT-5.4 Mini' :
    model === 'gpt-5.1-codex' ? 'GPT-5.1 Codex' :
    model === 'gpt-5.1-codex-mini' ? 'GPT-5.1 Codex Mini' :
    model === 'gpt-5.5' ? 'GPT-5.5 (best)' : model;
}

// THE ESCALATION TOGGLE (owner ruling 2026-07-28). ONE control, TWO states, default OFF.
// The three-state selector is retired and `frontier_only` is gone from the UI entirely:
// it was never a choice anyone should make — it named the state the system ENTERS when the
// local lane goes down, which the backend resilience floor handles and which this surface
// REPORTS below via localLaneUp. Escalation is opted into, never assumed: a frontier model
// has no domain memory, so leaving the box is frequently a downgrade.
const ESCALATION_TIP_OFF =
  'Escalation OFF (default) — everything runs local on the DGX. Nothing reaches the API. ' +
  'If the local lane goes down the OS falls back to the API automatically and says so.';
const ESCALATION_TIP_ON =
  'Escalation ON — local first, frontier when needed. The left model pill picks WHICH ' +
  'frontier model when the API is actually called.';

// D4 — local-down fallback dropdown. Same dropdown UX as the model pill: a compact
// pill shows the current target; the menu lists the options. `pill` is the short
// segment text; `full` is the menu-row label; `tip` spells out the behaviour.
type FallbackTarget = 'claude' | 'gpt' | 'none';
const FALLBACK_OPTIONS: {
  target: FallbackTarget;
  pill: string;
  full: string;
  tip: string;
}[] = [
  {
    target: 'claude',
    pill: 'CLAUDE',
    full: 'Claude (default)',
    tip: 'If the local model is down, fall back to Claude — keeps the OS alive.',
  },
  {
    target: 'gpt',
    pill: 'GPT',
    full: 'GPT',
    tip: 'If the local model is down, fall back to GPT.',
  },
  {
    target: 'none',
    pill: 'NONE',
    full: 'None — air-gap (stay local-only)',
    tip: 'No fallback — air-gap. Local down → clean dead-end, no API is ever called.',
  },
];
function fallbackPill(t: FallbackTarget): string {
  return FALLBACK_OPTIONS.find((o) => o.target === t)?.pill ?? t.toUpperCase();
}
function fallbackDotColor(t: FallbackTarget): string {
  return t === 'claude' ? 'bg-purple-400/70' :
    t === 'gpt' ? 'bg-emerald-400/70' :
    'bg-rexy-muted/50';
}

const InputBox: React.FC<InputBoxProps> = ({
  onSendMessage,
  onOverrideToggle,
  overrideMode,
  onFileUpload,
  onSendWithFiles,
  onSendWithFile,
  activeAgent,
  activeDomain,
  turnCount,
  authToken,
  uploadsDisabled,
  activeModel,
  setActiveModel,
  showModelMenu,
  setShowModelMenu,
  ALL_MODELS,
  overrideApi,
  showOverrideMenu,
  setShowOverrideMenu,
  routingMode,
  escalation,
  escalationLoaded,
  localLaneUp,
  toggleEscalation,
  fallbackTarget,
  setFallbackTarget,
  memoryOverride,
  onMemoryOverrideToggle,
  status,
  setShowMemoryManager,
  onFetchMemory,
  memoryDomain,
  onOpenSecurity,
  onQuit,
}) => {
  const [message, setMessage] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  const [isDriveUploading, setIsDriveUploading] = useState(false);
  const [driveUploadStatus, setDriveUploadStatus] = useState<string | null>(null);
  // CI-6: overflow menu open/close — purely local UI state.
  const [showOverflowMenu, setShowOverflowMenu] = useState(false);
  // D4 — local-down fallback dropdown open/close (local UI state, same as the
  // model pill's menu; the selected value itself is lifted App.tsx state).
  const [showFallbackMenu, setShowFallbackMenu] = useState(false);
  // R42 (frontier lanes, Round 6): where this chat has a lane pill, it is the model
  // choice and the model pill below is not drawn; the pill stays on a chat with no
  // lane pill (CODE, SECURITY, JOINT, no agent). Nothing is drawn for it until the
  // lane menu has answered, so it never flashes on a chat that has a lane pill.
  const [lanePill, setLanePill] = useState<LaneVisibility>('pending');
  const [isFocused, setIsFocused] = useState(false);
  const driveFileInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Close overflow on outside click (mirrors App.tsx's pattern for override/model menus).
  useEffect(() => {
    if (!showOverflowMenu) return;
    const handler = () => setShowOverflowMenu(false);
    const timer = setTimeout(() => {
      document.addEventListener('click', handler);
    }, 50);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', handler);
    };
  }, [showOverflowMenu]);

  // ── EXISTING HANDLERS — VERBATIM ────────────────────────────────────────

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setStagedFiles(prev => [...prev, ...Array.from(files)]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemoveStagedFile = (index: number) => {
    setStagedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const imageItems = Array.from(items).filter(item => item.type.startsWith('image/'));
    if (imageItems.length === 0) return;
    e.preventDefault();
    const newFiles: File[] = [];
    imageItems.forEach((item, idx) => {
      const blob = item.getAsFile();
      if (!blob) return;
      const ext = item.type.split('/')[1] || 'png';
      const file = new File([blob], `screenshot_${Date.now()}_${idx}.${ext}`, { type: item.type });
      newFiles.push(file);
    });
    if (newFiles.length > 0) {
      setStagedFiles(prev => [...prev, ...newFiles]);
    }
  };

  const handlePaperclipClick = () => {
    fileInputRef.current?.click();
  };

  const handleDriveUploadClick = () => {
    driveFileInputRef.current?.click();
  };

  const handleDriveFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (driveFileInputRef.current) driveFileInputRef.current.value = '';

    setIsDriveUploading(true);
    setDriveUploadStatus('Uploading to Drive...');

    try {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          // Protocol-keyed same-origin base (App.tsx pattern): HTTPS → page host no port,
          // HTTP → Flask :5000 direct (LAN path unchanged).
          const API_BASE = window.location.protocol === 'https:'
            ? `https://${window.location.host}/api`
            : `${window.location.protocol}//${window.location.hostname}:5000/api`;

          const dataUrl = reader.result as string;
          const base64 = dataUrl.split(',')[1];
          const ext = file.name.split('.').pop() || '';
          const res = await fetch(`${API_BASE}/drive/upload`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': authToken ? `Bearer ${authToken}` : '',
            },
            body: JSON.stringify({
              data: base64,
              filename: file.name,
              ext,
            }),
          });
          const data = await res.json();
          if (data.success) {
            setDriveUploadStatus(`✅ Uploaded to Drive: ${file.name}`);
            setTimeout(() => setDriveUploadStatus(null), 4000);
          } else {
            setDriveUploadStatus(`❌ Upload failed: ${data.error}`);
            setTimeout(() => setDriveUploadStatus(null), 5000);
          }
        } catch (err) {
          setDriveUploadStatus('❌ Upload error — check connection');
          setTimeout(() => setDriveUploadStatus(null), 5000);
        }
        setIsDriveUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      setIsDriveUploading(false);
      setDriveUploadStatus('❌ Upload failed');
      setTimeout(() => setDriveUploadStatus(null), 5000);
    }
  };

  const handleSend = async () => {
    const text = message.trim();
    if (!text && stagedFiles.length === 0) return;

    if (stagedFiles.length > 0) {
      setIsUploading(true);
      const filesToSend = [...stagedFiles];
      setStagedFiles([]);
      setMessage('');
      if (onSendWithFiles) {
        await onSendWithFiles(text, filesToSend);
      } else if (onSendWithFile) {
        for (const file of filesToSend) {
          await onSendWithFile(text, file);
        }
      } else if (onFileUpload) {
        for (const file of filesToSend) {
          await onFileUpload(file);
        }
      }
      setIsUploading(false);
    } else if (text) {
      onSendMessage(text);
      setMessage('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // ── THE HOLD-TO-TALK DICTATION BUTTON IS RETIRED (owner ruling 2026-08-14) ──
  //
  // `handleVoiceStart` / `handleVoiceStop` and the toolbar mic they drove are
  // gone, with the `isListening` state and the `recognitionRef` that only they
  // used. It was browser `SpeechRecognition` typing into this textarea — the
  // BROWSER's recogniser, not the box's: no socket, no daemon, no /api, no
  // media, and nothing in the voice lane ever saw it. It was unused.
  //
  // ITS PLACE ON AN AGENT SURFACE BELONGS TO A CALL BUTTON. Each agent becomes
  // directly callable from its own page, routing to that agent the way the
  // sphere routes to Rexy — a tap and a spoken handover pressing the same route.
  // Dictating text into a chat box is not that, and leaving it beside a call
  // button would offer two different things wearing one microphone.
  //
  // NOT REPLACED IN THIS FILE. The call button is its own component on the agent
  // surface, which is where the {domain, agent} pair actually lives; this bar is
  // the composer and takes no voice concern back.

  // ── DERIVED VISUALS ────────────────────────────────────────────────────

  const canSend = (message.trim().length > 0 || stagedFiles.length > 0) && !isUploading;
  const hasFiles = stagedFiles.length > 0;

  return (
    <div
      // CI-6 correction: lift the bar off the hard dark grey. Translucent
      // atmosphere-tinted glass so the bar reads as part of the chat surface
      // rather than a heavy slab. Matches the homepage console card treatment
      // and the new ChatThreadsBar surface for visual consistency.
      className={`flex flex-col gap-2 px-3 py-2.5 border-t transition-colors ${
        overrideMode ? 'border-orange-500/50' : ''
      }`}
      style={{
        background: 'rgba(8, 8, 24, 0.45)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderTopColor: overrideMode ? undefined : 'rgba(255,255,255,0.08)',
      }}
      onClick={(e) => e.stopPropagation()}
    >

      {/* Override mode banner — unchanged behaviour */}
      {overrideMode && (
        <div className="text-[10px] text-orange-400 font-bold uppercase
          tracking-widest text-center animate-pulse">
          ⚠ Override Mode Active — Qwen stepping aside, API answering directly
        </div>
      )}

      {/* Drive upload status — unchanged */}
      {driveUploadStatus && (
        <div className={`text-[10px] font-bold uppercase tracking-widest text-center ${
          driveUploadStatus.startsWith('✅') ? 'text-green-400' :
          driveUploadStatus.startsWith('❌') ? 'text-red-400' : 'text-blue-400 animate-pulse'
        }`}>
          {driveUploadStatus}
        </div>
      )}

      {/* Staged files preview — unchanged behaviour, lightly restyled.
          (Cannot occur when uploadsDisabled — the attach affordance is hidden.) */}
      {!uploadsDisabled && hasFiles && (
        <div className="flex flex-col gap-1.5">
          {stagedFiles.map((file, index) => {
            const isImage = file.type.startsWith('image/');
            const previewUrl = isImage ? URL.createObjectURL(file) : null;
            return (
              <div key={index} className="flex items-center gap-2 px-3 py-2 bg-rexy-dark
                border border-rexy-border/60 rounded-lg">
                {isImage && previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="preview"
                    className="w-8 h-8 object-cover rounded flex-shrink-0 border border-rexy-border"
                    onLoad={() => URL.revokeObjectURL(previewUrl)}
                  />
                ) : (
                  <svg className="w-4 h-4 text-rexy-accent flex-shrink-0" fill="none"
                    stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                )}
                <div className="flex-1 min-w-0">
                  <span className="text-[11px] text-rexy-text font-medium truncate block">
                    {file.name}
                  </span>
                  <span className="text-[9px] text-rexy-muted">
                    {isImage ? 'image' : file.name.split('.').pop()?.toUpperCase()} · {(file.size / 1024).toFixed(1)} KB · ready to send
                  </span>
                </div>
                <button
                  onClick={() => handleRemoveStagedFile(index)}
                  className="p-1 rounded hover:bg-rexy-border text-rexy-muted
                    hover:text-red-400 transition-colors flex-shrink-0"
                  title="Remove file"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor"
                    viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* iOS-shape rounded textarea — neutral card-tinted fill, neutral border
          (mirrors iOS InputBar TextInput which carries no accent). The
          accentColor/accentMuted props are accepted to mirror the iOS
          InputBar API exactly even though iOS doesn't visually apply them
          here — the accent in iOS chat lives in the header chip + bubbles. */}
      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        placeholder={hasFiles ? "Add a message with these files... (optional)" : "Message Rexy..."}
        rows={1}
        className={`w-full bg-rexy-dark text-rexy-text text-sm focus:outline-none resize-none
          transition-colors placeholder-rexy-muted px-4 py-3 rounded-2xl border
          min-h-[42px] max-h-[120px] ${
          isFocused ? 'border-rexy-text/30' : 'border-rexy-border'
        }`}
        onInput={(e) => {
          const target = e.target as HTMLTextAreaElement;
          target.style.height = 'auto';
          target.style.height = Math.min(target.scrollHeight, 120) + 'px';
        }}
      />

      {/* iOS toolbar: + · drive (spencer) · mic · model · spacer · ⋯ · send */}
      <div className="flex items-center gap-2">
        {/* + attach (paperclip) — hidden when uploadsDisabled (6.8 home chat) so
            there's no confusing pseudo-file-path where HOMEPAGE uploads are walled. */}
        {!uploadsDisabled && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.pdf,.docx,.doc,.txt,.md,.png,.jpg,.jpeg"
              multiple
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            <button
              onClick={handlePaperclipClick}
              disabled={isUploading}
              className={`w-9 h-9 rounded-full flex items-center justify-center transition-all ${
                hasFiles
                  ? 'text-rexy-accent border border-rexy-accent/40 bg-rexy-accent/10'
                  : isUploading
                  ? 'bg-rexy-dark text-rexy-accent animate-pulse border border-rexy-border'
                  : 'bg-rexy-dark text-rexy-muted border border-rexy-border hover:text-rexy-text'
              }`}
              title={hasFiles ? `${stagedFiles.length} file(s) staged — click to add more` : "Attach file(s)"}
            >
              {isUploading ? (
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                </svg>
              ) : (
                <span className="text-xl leading-none">+</span>
              )}
            </button>
          </>
        )}

        {/* Drive upload — Spencer-only, second-tier inline */}
        {activeAgent === 'spencer' && (
          <>
            <input
              ref={driveFileInputRef}
              type="file"
              accept=".xlsx,.xls,.pdf,.docx,.doc,.txt,.md,.png,.jpg,.jpeg"
              onChange={handleDriveFileSelect}
              style={{ display: 'none' }}
            />
            <button
              onClick={handleDriveUploadClick}
              disabled={isDriveUploading}
              className={`w-9 h-9 rounded-full flex items-center justify-center transition-all ${
                isDriveUploading
                  ? 'bg-blue-500/20 text-blue-400 animate-pulse border border-blue-500/30'
                  : 'bg-rexy-dark text-rexy-muted border border-rexy-border hover:text-blue-400'
              }`}
              title="Upload file to Partner Visa Drive folder"
            >
              {isDriveUploading ? (
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              )}
            </button>
          </>
        )}

        {/* The mic is retired — see the note beside the removed handlers above. */}

        {/* Model pill — opens existing showModelMenu dropdown; only on a chat with no
            lane pill (R42) */}
        {lanePill === 'hidden' && (
          <div className="relative" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setShowModelMenu((prev) => !prev)}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border border-rexy-border
                bg-rexy-dark hover:text-rexy-text text-rexy-muted text-[10px] font-bold uppercase tracking-widest
                transition-colors"
              title="Select model for current agent"
            >
              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${modelDotColor(activeModel)}`} />
              <span>{modelPill(activeModel)}</span>
              <span className="text-rexy-muted/60 leading-none">›</span>
            </button>
            {showModelMenu && (
              <div className="absolute bottom-full right-0 mb-2 w-48 bg-rexy-card border border-rexy-border
                rounded-lg shadow-xl z-50 overflow-hidden">
                <div className="px-3 py-2 text-[9px] text-rexy-muted font-bold uppercase
                  tracking-widest border-b border-rexy-border">
                  {activeAgent ? activeAgent.toUpperCase() : 'DEFAULT'} MODEL
                </div>
                {ALL_MODELS.map((m) => (
                  <button
                    key={m}
                    onClick={() => { setActiveModel(m); setShowModelMenu(false); }}
                    className={`w-full text-left px-3 py-2 text-[10px] font-bold
                      uppercase tracking-widest flex items-center gap-2 transition-colors ${
                      activeModel === m
                        ? 'bg-rexy-accent/20 text-rexy-accent'
                        : 'text-rexy-muted hover:text-rexy-text hover:bg-rexy-border'
                    }`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                      activeModel === m ? 'bg-rexy-accent' : modelDotColor(m)
                    }`} />
                    {modelFullLabel(m)}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Frontier lanes (Phase 4) — this agent's lane; where it shows, the chat's
            model choice (R42). */}
        <LaneMenu domain={activeDomain} agent={activeAgent} turnCount={turnCount} onVisibility={setLanePill} />

        <div className="flex-1" />

        {/* F1 Build B — three-state routing switch. RIGHT decides IF/HOW the API is
            used (routing); the LEFT model pill decides WHICH frontier model when the
            API is called. Segmented control: the active segment reads as pressed-in
            (inset shadow) + electric-blue accent when a frontier path is selected,
            green when local-only — dimensional, never a flat fill. */}
        <div
          className="flex items-center gap-0.5 p-0.5 rounded-full border border-rexy-border bg-rexy-dark"
          style={{ boxShadow: 'inset 0 1px 1px rgba(0,0,0,0.35), 0 1px 0 rgba(255,255,255,0.05)' }}
          title="Routing — decides IF/HOW the frontier API is used. The model pill (left) decides WHICH frontier model when the API is called."
          onClick={(e) => e.stopPropagation()}
        >
          {/* ONE toggle. Pressed-in when ON (electric blue), green when OFF (local) —
              dimensional, never a flat fill. Disabled until the server has answered:
              a control that renders before it knows is a surface asserting a guess. */}
          <button
            onClick={() => toggleEscalation(!escalation)}
            disabled={!escalationLoaded}
            title={escalationLoaded
              ? (escalation ? ESCALATION_TIP_ON : ESCALATION_TIP_OFF)
              : 'Reading the escalation setting from the OS…'}
            aria-pressed={escalation}
            role="switch"
            aria-checked={escalation}
            className={`px-2.5 py-1 rounded-full text-[9px] font-bold uppercase tracking-widest
              transition-all ${
              !escalationLoaded
                ? 'text-rexy-muted opacity-50 cursor-wait'
                : escalation
                  ? 'text-rexy-accent'
                  : 'text-rexy-success'
            }`}
            style={
              escalationLoaded
                ? {
                    background: escalation
                      ? 'rgba(0,112,243,0.20)'
                      : 'rgba(34,197,94,0.16)',
                    boxShadow:
                      'inset 0 1px 2px rgba(0,0,0,0.55), 0 1px 0 rgba(255,255,255,0.06)',
                  }
                : undefined
            }
          >
            {!escalationLoaded ? 'SYNCING' : escalation ? 'ESCALATION ON' : 'LOCAL ONLY'}
          </button>

          {/* THE OFFLINE STATE — reported, never offered. When the DGX local lane is down
              the OS is on the API by the resilience floor regardless of this toggle, and
              saying so is the whole point: a toggle reading "local only" while the backend
              is on the API would be a surface asserting something it has not verified. */}
          {escalationLoaded && !localLaneUp && (
            <span
              title="The local DGX lane is offline. The OS has defaulted to the API automatically — this is the offline default, not an escalation."
              className="px-2 py-1 rounded-full text-[9px] font-bold uppercase tracking-widest text-rexy-warning"
              style={{
                background: 'rgba(245,158,11,0.16)',
                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.55)',
              }}
            >
              LOCAL LANE OFFLINE — ON API
            </span>
          )}
        </div>

        {/* ── LOOP · CALL (owner ruling 2026-08-27) ──────────────────────────
            + · LOCAL · LOOP · CALL. The composer row is where the owner already
            is, and it frees the dashboard the two cards were taking a quarter of.
            DERIVED: the pair comes from AppShellLiveContext's navigation
            identity, so an agent created tomorrow gets both controls with no
            edit here — and ChatSurface.tsx is not touched to deliver it. */}
        <ComposerAgentControls />

        {/* D4 — local-down fallback dropdown. Same dropdown UX as the model pill
            (bottom-left): a compact pill shows the current target; clicking opens
            an upward menu of the three options. This is the seamless-continuity
            control — when the local model (Qwen) is down, the OS falls back here so
            it never goes dark. Depth matches the pill: raised pill, inset-pressed
            active row. */}
        {(() => {
          // frontier_only is retired: there is always a local path to fall back FROM,
          // so this control is never dimmed for that reason any more.
          const noLocal = false;
          return (
            <div
              className={`relative transition-opacity ${noLocal ? 'opacity-40' : 'opacity-100'}`}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowFallbackMenu((prev) => !prev)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border border-rexy-border
                  bg-rexy-dark hover:text-rexy-text text-rexy-muted text-[10px] font-bold uppercase tracking-widest
                  transition-colors"
                style={{ boxShadow: '0 1px 0 rgba(255,255,255,0.04)' }}
                title={
                  "If the local model is down, fall back to this. 'None' = air-gap, stay local-only." +
                  (noLocal ? ' (No local path under FRONTIER — this only applies when a local route is active.)' : '')
                }
              >
                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${fallbackDotColor(fallbackTarget)}`} />
                <span className="text-rexy-muted/70">FALLBACK</span>
                <span className="text-rexy-text/90">{fallbackPill(fallbackTarget)}</span>
                <span className="text-rexy-muted/60 leading-none">›</span>
              </button>
              {showFallbackMenu && (
                <div className="absolute bottom-full right-0 mb-2 w-56 bg-rexy-card border border-rexy-border
                  rounded-lg shadow-xl z-50 overflow-hidden">
                  <div className="px-3 py-2 text-[9px] text-rexy-muted font-bold uppercase
                    tracking-widest border-b border-rexy-border">
                    LOCAL-DOWN FALLBACK
                  </div>
                  {FALLBACK_OPTIONS.map((o) => {
                    const active = fallbackTarget === o.target;
                    return (
                      <button
                        key={o.target}
                        onClick={() => { setFallbackTarget(o.target); setShowFallbackMenu(false); }}
                        title={o.tip}
                        className={`w-full text-left px-3 py-2 text-[10px] font-bold
                          uppercase tracking-widest flex items-center gap-2 transition-colors ${
                          active
                            ? 'bg-rexy-accent/20 text-rexy-accent'
                            : 'text-rexy-muted hover:text-rexy-text hover:bg-rexy-border'
                        }`}
                        style={
                          active
                            ? { boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.45)' }
                            : undefined
                        }
                      >
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                          active ? 'bg-rexy-accent' : fallbackDotColor(o.target)
                        }`} />
                        {o.full}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })()}

        {/* ⋯ Overflow — Memory Override / Frontier / Memory Manager / Security / Quit */}
        <div className="relative" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => setShowOverflowMenu((prev) => !prev)}
            className="w-9 h-9 rounded-full flex items-center justify-center transition-all
              bg-rexy-dark text-rexy-muted border border-rexy-border hover:text-rexy-text"
            title="More controls"
          >
            <span className="text-base leading-none">⋯</span>
          </button>

          {/* F1 Build B — the API-override routing sub-dropdown was removed here.
              Routing is now the three-state switch to the left of this overflow;
              the frontier MODEL is chosen via the model pill. This also retires the
              old literal-'CLAUDE' override payload bug (no caller passes it anymore). */}

          {/* Overflow menu */}
          {showOverflowMenu && (
            <div className="absolute bottom-full right-0 mb-2 bg-rexy-card border border-rexy-border
              rounded-lg shadow-xl z-50 overflow-hidden min-w-[220px]">

              {/* Memory Override toggle */}
              <button
                onClick={(e) => { e.stopPropagation(); onMemoryOverrideToggle(); }}
                className="w-full text-left px-3 py-2 text-[10px] font-bold uppercase
                  tracking-widest flex items-center gap-2 transition-colors
                  text-rexy-muted hover:text-rexy-text hover:bg-rexy-border/30"
                title={memoryOverride
                  ? 'Memory Override ON — contradiction detection suspended'
                  : 'Memory Override OFF — normal governance active'}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${memoryOverride ? 'bg-orange-400 animate-pulse' : 'bg-rexy-muted/50'}`} />
                <span className="flex-1">Memory Override</span>
                <span className={memoryOverride ? 'text-orange-400' : 'text-rexy-muted/70'}>
                  {memoryOverride ? 'ON' : 'OFF'}
                </span>
              </button>

              {/* Frontier indicator — only when active */}
              {status?.frontier_api && (
                <div className="px-3 py-2 text-[10px] font-bold uppercase tracking-widest
                  flex items-center gap-2 text-rexy-muted/70 bg-rexy-border/20">
                  <span className="w-1.5 h-1.5 rounded-full bg-rexy-accent/70" />
                  <span className="flex-1">Frontier</span>
                  <span className="text-rexy-accent">{status.frontier_api}</span>
                </div>
              )}

              <div className="border-t border-rexy-border" />

              {/* Memory Manager */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowOverflowMenu(false);
                  setShowMemoryManager(true);
                  onFetchMemory(memoryDomain);
                }}
                className="w-full text-left px-3 py-2 text-[10px] font-bold uppercase
                  tracking-widest flex items-center gap-2 transition-colors
                  text-rexy-muted hover:text-rexy-text hover:bg-rexy-border/30"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-rexy-muted/50" />
                Memory Manager
              </button>

              {/* Security Dashboard */}
              <button
                onClick={(e) => { e.stopPropagation(); setShowOverflowMenu(false); onOpenSecurity(); }}
                className="w-full text-left px-3 py-2 text-[10px] font-bold uppercase
                  tracking-widest flex items-center gap-2 transition-colors
                  text-rexy-muted hover:text-rexy-text hover:bg-rexy-border/30"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-rexy-muted/50" />
                Security Dashboard
              </button>

              <div className="border-t border-rexy-border" />

              {/* Quit — danger row */}
              <button
                onClick={(e) => { e.stopPropagation(); setShowOverflowMenu(false); onQuit(); }}
                className="w-full text-left px-3 py-2 text-[10px] font-bold uppercase
                  tracking-widest flex items-center gap-2 transition-colors
                  text-rexy-muted hover:text-red-400 hover:bg-red-900/20"
                title="Quit Rexy — closes app and releases all services"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-red-400/60" />
                Quit
              </button>
            </div>
          )}
        </div>

        {/* Send — iOS action-orange circle with ↑ */}
        <button
          onClick={handleSend}
          disabled={!canSend}
          className="w-9 h-9 rounded-full flex items-center justify-center
            disabled:opacity-30 disabled:cursor-not-allowed transition-all active:scale-95"
          style={{ backgroundColor: '#f97316' }}
          title="Send"
        >
          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 19V5m0 0l-7 7m7-7l7 7" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default InputBox;
